import { createContext, useContext } from 'react';
import { api, setAuthToken } from './client';
import { forgetKeptLoads } from './loader';
import { forgetKeptLists } from './paging';
import { STAFF_ROLES, type MeResponse, type SessionResponse, type SessionUser } from './types';

/*
 * Старт сессии: меняем launch-параметры ВК на наш JWT.
 *
 * ТОКЕН ХРАНИТСЯ ТОЛЬКО В ПАМЯТИ. Ни localStorage, ни sessionStorage, ни
 * куки: хостинг ВК выдаёт новый домен на каждый деплой, вместе с доменом
 * теряется и хранилище — «сохранённый» токен всё равно не переживёт
 * обновление приложения, зато успеет протухнуть и сломать вход. Плюс это
 * лишняя копия токена там, где её может прочитать чужой скрипт.
 * Перезапуск мини-приложения стоит один POST — этого достаточно.
 * Пожалуйста, не «чините» это обратно на localStorage.
 */

export interface Session {
  user: SessionUser | null;
  /** true = аккаунта нет в вайтлисте: только чтение. */
  guest: boolean;
  vkUserId: number;
  /** Открыта ли самостоятельная запись: гость увидит кнопку «присоединиться». */
  selfRegistration: boolean;
}

/**
 * Параметры запуска ВК. Знание о том, ГДЕ они лежат, живёт в одном месте:
 * их шлёт и старт сессии, и саморегистрация. В query, а не в хеше — при
 * хеш-роутинге туда же.
 */
export function launchParams(): string {
  return window.location.search;
}

let pending: Promise<Session> | null = null;

/**
 * Стартует сессию один раз за жизнь вкладки. Повторные вызовы (React
 * StrictMode зовёт эффекты дважды) получают тот же промис, а не второй POST.
 */
export function startSession(): Promise<Session> {
  pending ??= createSession();
  return pending;
}

async function createSession(): Promise<Session> {
  /*
   * Дев-вход по готовому токену.
   *
   * Вне ВК launch-параметров нет, и POST /api/auth/session отбивается — то
   * есть открыть приложение в обычном браузере нельзя вообще, ни посмотреть
   * вёрстку, ни пройтись по экранам. Токен берут из кабинета или из лога и
   * передают ПЕРЕМЕННОЙ ОКРУЖЕНИЯ, разово:
   *
   *   VITE_DEV_TOKEN=<токен> npm run web
   *
   * Именно переменной, а не параметром адреса: в адресе токен осел бы в
   * истории браузера и уехал бы в Referer.
   *
   * В прод не попадёт: import.meta.env.DEV на сборке статически false, и
   * вся ветка выкидывается из бандла как мёртвый код.
   */
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_TOKEN) {
    setAuthToken(import.meta.env.VITE_DEV_TOKEN);
    const me = await api.get<MeResponse>('/api/auth/me');
    return {
      user: me.user,
      guest: me.user === null,
      vkUserId: me.user?.vkUserId ?? 0,
      selfRegistration: me.selfRegistration,
    };
  }

  // launchParams уходят телом, а не в URL: в URL они попали бы в логи и Referer.
  // Параметры запуска ВК всегда лежат в query, даже при хеш-роутинге.
  const session = await api.post<SessionResponse>('/api/auth/session', {
    launchParams: launchParams(),
  });

  setAuthToken(session.token);

  // Роль и право записи берём из /me: там они перечитаны из базы, а не
  // из полей токена, который мы только что получили.
  let user = session.user;
  let selfRegistration = false;
  try {
    const me = await api.get<MeResponse>('/api/auth/me');
    user = me.user;
    selfRegistration = me.selfRegistration;
  } catch {
    // /me не ответил — работаем с тем, что вернул старт сессии. Кнопки
    // «присоединиться» гость при этом не увидит: без ответа считаем, что
    // запись закрыта.
  }

  return { user, guest: user === null, vkUserId: session.vkUserId, selfRegistration };
}

/**
 * Сбрасывает сессию перед повторным стартом — после ошибки, записи в участники
 * или удаления данных. Прочитанные списки и ответы уходят вместе с токеном: их
 * отдавали прежней роли.
 */
export function resetSession(): void {
  pending = null;
  setAuthToken(null);
  forgetKeptLists();
  forgetKeptLoads();
}

/* ── Сессия внутри React ─────────────────────────────────────────────────── */

/** Провайдер ставит App, когда сессия уже получена. */
export const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession вызван вне SessionContext.Provider');
  return session;
}

/** Видит ли аккаунт вкладку «Задачи». */
export function isStaff(session: Session): boolean {
  return session.user !== null && STAFF_ROLES.includes(session.user.role);
}
